import { pbQuery } from "@sergio9929/pb-query";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { pb } from "@/lib/pocketbase";
import type { SafeOmit } from "@/lib/utils";
import { appointmentsQueries } from "@/modules/appointments/queries";
import {
	type AppointmentsResponse,
	Collections,
	type DoctorsResponse,
	type PatientsResponse,
} from "@/types/pocketbase";

// --- Types ---

type ExpandDoctorAndPatient = {
	patient: PatientsResponse;
	doctor: DoctorsResponse;
	startDate: Date;
};

type WithPatientAndDoctor = {
	patient: PatientsResponse;
	doctor: DoctorsResponse;
};

type Search = SafeOmit<AppointmentsResponse, "patient" | "doctor" | "startDate"> & ExpandDoctorAndPatient;

type useEventsProps = {
	startDate: Date;
	endDate: Date;
	currentPatientId?: string;
	activeBranchId?: string;
};

export const eventsQueryOptions = ({ startDate, endDate, activeBranchId, currentPatientId }: useEventsProps) => {
	const dynamicQuery = pbQuery<Search>()
		.expand(["doctor", "patient"])
		.between("startDate", startDate, endDate)
		.sort("-startDate");

	if (currentPatientId) {
		dynamicQuery.and().equal("patient", currentPatientId);
	}

	if (activeBranchId) {
		dynamicQuery.and().equal("branch", activeBranchId);
	}

	const query = dynamicQuery.build((raw, params) => pb.filter(raw, params));

	const appointmentsQuery = queryOptions({
		queryKey: [...appointmentsQueries.fullLists(), "fullCalendar", query],
		queryFn: async () => {
			const appointments = await pb
				.collection(Collections.Appointments)
				.getFullList<AppointmentsResponse<WithPatientAndDoctor>>({
					...query,
					sort: "-created",
				});

			return appointments;
		},
		placeholderData: keepPreviousData,
	});

	return appointmentsQuery;
};
